import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

const EMAIL_API_URL = "https://wealth-builder-backend-939575270287.us-east1.run.app/api/v1/email/send";

/**
 * Send email using the backend API
 * @param {Object} params - Email parameters
 * @param {string} params.to - Recipient email
 * @param {string} params.subject - Email subject
 * @param {string} params.body - Email body (HTML)
 * @param {string} params.from - Sender email (default: info@dhabbi.com)
 * @returns {Promise<Object>} API response
 */
async function sendEmailViaAPI({ to, subject, body, from = "info@dhabbi.com" }) {
  try {
    console.log(`📧 Sending email via API to: ${to}`);
    
    const response = await fetch(EMAIL_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to,
        from,
        subject,
        body
      })
    });

    if (!response.ok) {
      throw new Error(`Email API failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log(`✅ Email sent successfully to ${to}`);
    return result;
  } catch (error) {
    console.error(`❌ Failed to send email to ${to}:`, error);
    throw error;
  }
}

/**
 * Send email using Firebase Extension (trigger-email-from-firestore)
 * The extension watches the 'mail' collection and sends emails automatically
 * @deprecated Use sendEmailViaAPI instead
 */

/**
 * Send a public Insight Center invitation email
 * @param {Object} params - Email parameters
 * @param {string} params.toEmail - Recipient email
 * @param {string} params.toName - Recipient name
 * @param {string} params.fromName - Sender name (optional)
 * @returns {Promise<Object>} API response
 */
export async function sendPublicInsightCenterInvite({ toEmail, toName, fromName = "Wealth Builders Team" }) {
  console.log(`📧 Sending public invite email to: ${toEmail}`);
  const publicUrl = `${window.location.origin}/public-insight-center`;
  
  const htmlBody = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <style>
              body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; line-height: 1.6; color: #333; }
              .container { max-width: 600px; margin: 0 auto; padding: 20px; }
              .header { background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%); padding: 40px 20px; text-align: center; border-radius: 8px 8px 0 0; }
              .logo { color: #d4af37; font-size: 28px; font-weight: bold; margin: 0; }
              .content { background: #ffffff; padding: 40px 30px; border-left: 1px solid #e5e5e5; border-right: 1px solid #e5e5e5; }
              .greeting { font-size: 20px; font-weight: 600; color: #1a1a1a; margin-bottom: 20px; }
              .message { font-size: 16px; color: #4a5568; margin-bottom: 30px; line-height: 1.8; }
              .cta-button { display: inline-block; padding: 16px 40px; background: linear-gradient(135deg, #d4af37 0%, #f5d66a 100%); color: #000; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 16px; margin: 20px 0; box-shadow: 0 4px 12px rgba(212, 175, 55, 0.3); }
              .cta-button:hover { box-shadow: 0 6px 20px rgba(212, 175, 55, 0.4); }
              .footer { background: #f7fafc; padding: 30px; text-align: center; border-radius: 0 0 8px 8px; border: 1px solid #e5e5e5; }
              .footer-text { font-size: 14px; color: #718096; margin: 5px 0; }
              .highlight { color: #d4af37; font-weight: 600; }
              ul { margin: 20px 0; padding-left: 20px; }
              li { margin: 10px 0; color: #4a5568; }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="header">
                <h1 class="logo">💎 WEALTH BUILDERS</h1>
              </div>
              
              <div class="content">
                <div class="greeting">Hi ${toName}! 👋</div>
                
                <div class="message">
                  <p>You've been invited by <strong>${fromName}</strong> to explore an exclusive opportunity with <span class="highlight">Wealth Builders</span>.</p>
                  
                  <p>We're excited to share our <strong>Insight Center</strong> with you - a comprehensive platform designed to help you:</p>
                  
                  <ul>
                    <li>🎯 Build financial independence</li>
                    <li>📚 Access world-class training</li>
                    <li>💼 Create sustainable income</li>
                    <li>🌟 Join a thriving community</li>
                  </ul>
                  
                  <p>Click below to watch our introductory video and learn how Wealth Builders can transform your financial future:</p>
                  
                  <center>
                    <a href="${publicUrl}" class="cta-button">
                      🚀 Explore Wealth Builders
                    </a>
                  </center>
                  
                  <p style="margin-top: 30px; font-size: 14px; color: #718096;">
                    <em>No login required - watch instantly!</em>
                  </p>
                </div>
              </div>
              
              <div class="footer">
                <p class="footer-text"><strong>Wealth Builders</strong></p>
                <p class="footer-text">Building Financial Freedom, One Agent at a Time</p>
                <p class="footer-text" style="margin-top: 20px; font-size: 12px;">
                  This email was sent to you because someone believes you'd be a great fit for Wealth Builders.
                </p>
              </div>
            </div>
          </body>
        </html>
      `;

  return await sendEmailViaAPI({
    to: toEmail,
    subject: `${toName}, You're Invited to Explore Wealth Builders! 🌟`,
    body: htmlBody
  });
}

/**
 * Send welcome email for new agent with account setup instructions
 * @param {Object} params - Email parameters
 * @param {string} params.toEmail - Recipient email
 * @param {string} params.toName - Recipient name
 * @param {string} params.agencyCode - Agency code
 * @param {string} params.recruiterName - Recruiter name
 * @returns {Promise<Object>} API response
 */
export async function sendAgentWelcomeEmail({ toEmail, toName, agencyCode, recruiterName = "Your Leader" }) {
  console.log(`📧 Sending welcome email to new agent: ${toEmail}`);
  const loginUrl = `${window.location.origin}/login`;
  
  const htmlBody = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <style>
              body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; line-height: 1.6; color: #333; }
              .container { max-width: 600px; margin: 0 auto; padding: 20px; }
              .header { background: linear-gradient(135deg, #d4af37 0%, #f5d66a 100%); padding: 40px 20px; text-align: center; border-radius: 8px 8px 0 0; }
              .logo { color: #000; font-size: 28px; font-weight: bold; margin: 0; }
              .content { background: #ffffff; padding: 40px 30px; border-left: 1px solid #e5e5e5; border-right: 1px solid #e5e5e5; }
              .greeting { font-size: 22px; font-weight: 600; color: #1a1a1a; margin-bottom: 20px; }
              .message { font-size: 16px; color: #4a5568; margin-bottom: 25px; line-height: 1.8; }
              .info-box { background: #f7fafc; border-left: 4px solid #d4af37; padding: 20px; margin: 25px 0; border-radius: 4px; }
              .info-label { font-weight: 600; color: #2d3748; margin-bottom: 5px; }
              .info-value { font-size: 18px; color: #d4af37; font-weight: 700; font-family: monospace; }
              .cta-button { display: inline-block; padding: 16px 40px; background: linear-gradient(135deg, #d4af37 0%, #f5d66a 100%); color: #000; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 16px; margin: 20px 0; box-shadow: 0 4px 12px rgba(212, 175, 55, 0.3); }
              .steps { background: #f7fafc; padding: 25px; border-radius: 8px; margin: 25px 0; }
              .step { margin: 15px 0; padding-left: 30px; position: relative; }
              .step-number { position: absolute; left: 0; top: 0; background: #d4af37; color: #000; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 14px; }
              .footer { background: #1a1a1a; padding: 30px; text-align: center; border-radius: 0 0 8px 8px; }
              .footer-text { font-size: 14px; color: #a0aec0; margin: 5px 0; }
              .highlight { color: #d4af37; font-weight: 600; }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="header">
                <h1 class="logo">💎 WEALTH BUILDERS</h1>
                <p style="color: #000; margin: 10px 0 0 0; font-size: 16px;">Welcome to Your New Journey!</p>
              </div>
              
              <div class="content">
                <div class="greeting">🎊 Congratulations, ${toName}!</div>
                
                <div class="message">
                  <p>Welcome to the <strong>Wealth Builders</strong> family! <span class="highlight">${recruiterName}</span> has added you to our team, and we're thrilled to have you on board.</p>
                  
                  <p>Your account has been created and you're ready to get started. Here's what you need to know:</p>
                </div>
                
                <div class="info-box">
                  <div class="info-label">📧 Your Email</div>
                  <div class="info-value">${toEmail}</div>
                </div>
                
                <div class="info-box">
                  <div class="info-label">🔑 Your Agency Code</div>
                  <div class="info-value">${agencyCode}</div>
                </div>
                
                <div class="steps">
                  <h3 style="margin-top: 0; color: #2d3748;">Next Steps to Get Started:</h3>
                  
                  <div class="step">
                    <div class="step-number">1</div>
                    <strong>Set Your Password</strong><br>
                    <span style="color: #718096;">Click the button below to access the login page and create your password.</span>
                  </div>
                  
                  <div class="step">
                    <div class="step-number">2</div>
                    <strong>Complete Your Profile</strong><br>
                    <span style="color: #718096;">Upload a profile picture and fill in your information.</span>
                  </div>
                  
                  <div class="step">
                    <div class="step-number">3</div>
                    <strong>Explore the Platform</strong><br>
                    <span style="color: #718096;">Access training materials, trackers, and tools to build your business.</span>
                  </div>
                  
                  <div class="step">
                    <div class="step-number">4</div>
                    <strong>Connect with ${recruiterName}</strong><br>
                    <span style="color: #718096;">Your leader is here to help you succeed every step of the way.</span>
                  </div>
                </div>
                
                <center>
                  <a href="${loginUrl}" class="cta-button">
                    🚀 Set Up My Account
                  </a>
                </center>
                
                <div class="message" style="margin-top: 30px;">
                  <p><strong>Questions?</strong> Reach out to <span class="highlight">${recruiterName}</span> or explore our training center once you log in.</p>
                  
                  <p style="font-size: 14px; color: #718096; margin-top: 25px;">
                    <em>Keep this email safe - your agency code is important for accessing certain features.</em>
                  </p>
                </div>
              </div>
              
              <div class="footer">
                <p class="footer-text" style="color: #d4af37; font-weight: bold; font-size: 16px;">WEALTH BUILDERS</p>
                <p class="footer-text">Building Financial Freedom Together</p>
                <p class="footer-text" style="margin-top: 20px;">
                  Need help? Contact your leader or visit our support center.
                </p>
              </div>
            </div>
          </body>
        </html>
      `;

  return await sendEmailViaAPI({
    to: toEmail,
    subject: `🎉 Welcome to Wealth Builders, ${toName}!`,
    body: htmlBody
  });
}

/**
 * Send trainer assignment notification email
 * @param {Object} params - Email parameters
 * @param {string} params.trainerEmail - Trainer's email
 * @param {string} params.trainerName - Trainer's name
 * @param {string} params.prospectName - Prospect/client name
 * @param {string} params.appointmentType - Type of appointment
 * @param {string} params.appointmentDate - Date of appointment
 * @param {string} params.appointmentTime - Time of appointment
 * @param {string} params.meetingType - Meeting type (Zoom, Phone, etc.)
 * @param {string} params.meetingUrl - Meeting URL (if applicable)
 * @param {string} params.notes - Additional notes
 * @param {string} params.assignedBy - Name of person who assigned
 * @returns {Promise<Object>} API response
 */
export async function sendTrainerAssignmentEmail({ 
  trainerEmail, 
  trainerName, 
  prospectName, 
  appointmentType, 
  appointmentDate, 
  appointmentTime, 
  meetingType,
  meetingUrl,
  notes,
  assignedBy = "Wealth Builders Team"
}) {
  console.log(`📧 Sending trainer assignment email to: ${trainerEmail}`);
  
  const meetingDetails = meetingUrl ? 
    `<p><strong>Meeting Link:</strong> <a href="${meetingUrl}" style="color: #d4af37;">${meetingUrl}</a></p>` :
    `<p><strong>Meeting Type:</strong> ${meetingType}</p>`;
  
  const htmlBody = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <style>
              body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; line-height: 1.6; color: #333; }
              .container { max-width: 600px; margin: 0 auto; padding: 20px; }
              .header { background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%); padding: 40px 20px; text-align: center; border-radius: 8px 8px 0 0; }
              .logo { color: #d4af37; font-size: 28px; font-weight: bold; margin: 0; }
              .content { background: #ffffff; padding: 40px 30px; border-left: 1px solid #e5e5e5; border-right: 1px solid #e5e5e5; }
              .greeting { font-size: 20px; font-weight: 600; color: #1a1a1a; margin-bottom: 20px; }
              .message { font-size: 16px; color: #4a5568; margin-bottom: 20px; line-height: 1.8; }
              .appointment-card { background: #f7fafc; border-left: 4px solid #d4af37; padding: 20px; margin: 25px 0; border-radius: 8px; }
              .appointment-card p { margin: 10px 0; font-size: 15px; }
              .appointment-card strong { color: #1a1a1a; }
              .notes-box { background: #fffbeb; border: 1px solid #fcd34d; padding: 15px; margin: 20px 0; border-radius: 6px; }
              .notes-box strong { color: #92400e; }
              .cta-button { display: inline-block; padding: 16px 40px; background: linear-gradient(135deg, #d4af37 0%, #f5d66a 100%); color: #000; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 16px; margin: 20px 0; box-shadow: 0 4px 12px rgba(212, 175, 55, 0.3); }
              .footer { background: #f7fafc; padding: 30px; text-align: center; border-radius: 0 0 8px 8px; border: 1px solid #e5e5e5; }
              .footer-text { font-size: 14px; color: #718096; margin: 5px 0; }
              .highlight { color: #d4af37; font-weight: 600; }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="header">
                <h1 class="logo">💎 WEALTH BUILDERS</h1>
              </div>
              
              <div class="content">
                <div class="greeting">Hi ${trainerName}! 👋</div>
                
                <div class="message">
                  <p>You've been assigned as a <span class="highlight">trainer</span> for a new training request by <strong>${assignedBy}</strong>.</p>
                </div>

                <div class="appointment-card">
                  <h3 style="margin-top: 0; color: #1a1a1a;">📅 Training Details</h3>
                  <p><strong>Prospect/Client:</strong> ${prospectName}</p>
                  <p><strong>Appointment Type:</strong> ${appointmentType}</p>
                  <p><strong>Date:</strong> ${appointmentDate}</p>
                  <p><strong>Time:</strong> ${appointmentTime}</p>
                  ${meetingDetails}
                </div>

                ${notes ? `
                <div class="notes-box">
                  <strong>📝 Notes:</strong>
                  <p style="margin: 10px 0 0 0; color: #78350f;">${notes}</p>
                </div>
                ` : ''}

                <div class="message">
                  <p>Please review the training details and prepare accordingly. If you have any questions or need to reschedule, please reach out to ${assignedBy}.</p>
                </div>
              </div>
              
              <div class="footer">
                <p class="footer-text" style="color: #d4af37; font-weight: bold; font-size: 16px;">WEALTH BUILDERS</p>
                <p class="footer-text">Building Financial Freedom Together</p>
                <p class="footer-text" style="margin-top: 20px;">
                  Need help? Contact ${assignedBy} or visit our support center.
                </p>
              </div>
            </div>
          </body>
        </html>
      `;

  return await sendEmailViaAPI({
    to: trainerEmail,
    subject: `🎓 New Training Assignment: ${prospectName} - ${appointmentType}`,
    body: htmlBody
  });
}
